import { locRecord } from "#src/extensions/filter-items.js";
import extension from "@local/extensions/utilities/extension.js";
import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
import imgClipToBlob from "#src/extensions/dom/img-clip-to-blob.js";
import { api } from "#src/api";
import { sheetLabel } from "#lib/framex/sheet-label.js";

async function imageDrawingFromClip(location, doc) {
  const page = doc.data.pages[location.page_id];
  const f = await api.storage.from("documents").download(page.name);
  if (f.error) return;

  const url = URL.createObjectURL(f.data);
  const blob = await imgClipToBlob(url, location.clip);
  const clipId = crypto.randomUUID();
  const object_id = `${page.name}/${clipId}.png`;

  const stored = await api.storage.from("drawings").upload(object_id, blob, {
    contentType: "image/png",
  });
  if (stored.error) return;

  return {
    attachment_id: location.id,
    id: clipId,
    name: sheetLabel(page) || "clipped image",
    type: "image",
    extension: "png",
    object_id,
    preview_object_id: object_id,
    clip: location.clip,
    properties: {
      width: location.clip.width,
      height: location.clip.height,
    },
  };
}

// The clip drawing for a location whose records have not been added to the
// store yet — the insert-time counterpart to locationDrawingUpdate, which can
// only run once both records have landed. Returns null when there is nothing
// to clip or the image could not be built.
async function drawingForClip(location, doc) {
  const page = doc?.data?.pages?.[location?.page_id];
  if (!location?.clip || !page) return null;

  const ext = extension(page.name);
  if (ext !== "pdf") return (await imageDrawingFromClip(location, doc)) || null;

  return {
    attachment_id: location.id,
    id: page.name.split("/")[0],
    name: sheetLabel(page) || "clipped image",
    type: "iiif",
    clip: location.clip,
    path: `${nameMinusExtension(page.name)}.${page.page}.tiff`,
    extension: ext,
    properties: {
      width: location.clip.width,
      height: location.clip.height,
    },
  };
}

async function locationDrawingUpdate(locid, grp) {
  const loc = grp.locations[locid];
  if (!loc) return null;

  const record = locRecord(loc, grp);
  const doc = grp.documents[loc.document_id];
  const page = doc.data.pages[loc.page_id];

  if (!record || !doc || !page) return null;

  if (record.drawing && record.drawing.attachment_id === loc.id) {
    if (loc.record_type === "type") {
      return { type: "type", id: record.id, prop: "drawing", value: null };
    } else if (loc.record_type === "document_note") {
      return {
        type: "document_note",
        id: record.id,
        prop: "drawing",
        value: null,
      };
    } else {
      return { type: "item", id: record.id, prop: "drawing", value: null };
    }
  }

  const ext = extension(page.name);
  if (ext === "pdf") {
    const path = `${nameMinusExtension(page.name)}.${page.page}.tiff`;

    const drawing = {
      attachment_id: loc.id,
      id: page.name.split("/")[0],
      name: sheetLabel(page) || "clipped image",
      type: "iiif",
      clip: loc.clip,
      path,
      extension: ext,
      properties: {
        width: loc.clip.width,
        height: loc.clip.height,
      },
    };

    if (loc.record_type === "type") {
      return { type: "type", id: record.id, prop: "drawing", value: drawing };
    } else if (loc.record_type === "document_note") {
      return {
        type: "document_note",
        id: record.id,
        prop: "drawing",
        value: drawing,
      };
    } else {
      return { type: "item", id: record.id, prop: "drawing", value: drawing };
    }
  } else {
    const drawing = await imageDrawingFromClip(loc, doc);
    if (loc.record_type === "type") {
      return { type: "type", id: record.id, prop: "drawing", value: drawing };
    } else if (loc.record_type === "document_note") {
      return {
        type: "document_note",
        id: record.id,
        prop: "drawing",
        value: drawing,
      };
    } else {
      return { type: "item", id: record.id, prop: "drawing", value: drawing };
    }
  }
}

export { locationDrawingUpdate, imageDrawingFromClip, drawingForClip };
